import { useState, useEffect } from 'react';
import { IIrpItemAddEditRequest } from '../../../../../model/scp/irp/request/irp-item-add-edit-request';
import { IIrpItemResponse } from '../../../../../model/scp/irp/response/irp-item-response';
import { ITableResponse } from '../../../../../model/scp/purchase/response/tables-response';
import BaseAppService from '../../../../../application/platform/scp/base/base-app-service';
import IrpAppService from '../../../../../application/platform/scp/irp/irp-app-service';
import { useFeedback } from '../../../../../hooks/use-feedback';
import { sanitizeText } from '../../../../../utils/text';
import { hasErrors } from '../../../../../utils/validation';
import { validateIrpItem } from '../../context/irp-validation';

const ITEMS_PAGE_SIZE = 50;
const EMPTY_ITEM: IIrpItemAddEditRequest = { applicabilityMarginPreference: false, requirementNationalContent: false, indConfidentialValue: false };

function parseDraftItems(json?: string): IIrpItemResponse[] {
    try {
        const items = JSON.parse(json || '[]');
        return Array.isArray(items) ? items : [];
    } catch {
        return [];
    }
}

function toAddFormat(item: IIrpItemAddEditRequest): IIrpItemAddEditRequest {
    return {
        typeItem: item.typeItem,
        categoryItemId: item.categoryItemId,
        criterionJudgmentId: item.criterionJudgmentId,
        applicabilityMarginPreference: item.applicabilityMarginPreference || false,
        requirementNationalContent: item.requirementNationalContent || false,
        description: item.description || '',
        unitSupply: item.unitSupply || '',
        unitValueEstimated: item.unitValueEstimated || 0,
        indConfidentialValue: item.indConfidentialValue || false,
        gtinCode: item.gtinCode,
        ncmNbsCode: item.ncmNbsCode,
        ncmNbsDescription: item.ncmNbsDescription,
        catalogId: item.catalogId,
        catalogItemCode: item.catalogItemCode,
    };
}

function toRetifyFormat(item: IIrpItemAddEditRequest): IIrpItemAddEditRequest {
    return {
        number: item.number,
        description: item.description || '',
        complementaryInformation: item.complementaryInformation || '',
        unitSupply: item.unitSupply || '',
        unitValueEstimated: item.unitValueEstimated || 0,
    };
}

const FIELD_BY_INPUT: Record<string, keyof IIrpItemAddEditRequest> = {
    'select-type-item': 'typeItem',
    'select-category-item': 'categoryItemId',
    'select-criterion-judgment': 'criterionJudgmentId',
    'input-description': 'description',
    'input-complementary-information-item': 'complementaryInformation',
    'input-unit-supply': 'unitSupply',
    'input-justification-item': 'justification',
};
const SANITIZED_INPUTS = new Set(['input-description', 'input-complementary-information-item', 'input-justification-item']);
const CHECK_BY_INPUT: Record<string, keyof IIrpItemAddEditRequest> = {
    'check-applicability-margin-preference': 'applicabilityMarginPreference',
    'check-requirement-national-content': 'requirementNationalContent',
    'check-ind-confidential-value': 'indConfidentialValue',
};

// Itens da IRP: em rascunho (antes de salvar a IRP) ficam em memória; depois são enviados um a um ao PNCP
export const useIrpItemController = (
    irpId: string | undefined,
    modalityId: number | null | undefined,
    setItemsIrpJson: (value: string) => void,
    initialItemsJson?: string
) => {
    const { showError, showSuccess, confirmDelete, confirmDeleteWithJustification, withLoading } = useFeedback();
    const isDraft = irpId === undefined;
    const [itemIrp, setItemIrp] = useState<IIrpItemAddEditRequest>(EMPTY_ITEM);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [materialService, setMaterialService] = useState<ITableResponse[]>([]);
    const [categoryItemPca, setCategoryItemPca] = useState<ITableResponse[]>([]);
    const [judgementCriteria, setJudgementCriteria] = useState<ITableResponse[]>([]);
    const [irpItems, setIrpItems] = useState<IIrpItemResponse[]>(() => (isDraft ? parseDraftItems(initialItemsJson) : []));
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const isRetification = !isDraft && itemIrp.number !== undefined;

    useEffect(() => {
        loadModalityTables();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalityId]);

    useEffect(() => {
        BaseAppService.listCategoryItemPca()
            .then(setCategoryItemPca)
            .catch((error: any) => showError('Não foi possível carregar as categorias de item', error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [irpId, page]);

    useEffect(() => {
        if (isDraft) setItemsIrpJson(JSON.stringify(irpItems));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [irpItems]);

    async function loadModalityTables() {
        try {
            const [materialServiceList, judgementCriteriaList] = await Promise.all([
                BaseAppService.listMaterialService(modalityId),
                BaseAppService.listJudgmentCriteria(modalityId),
            ]);
            setMaterialService(materialServiceList);
            setJudgementCriteria(judgementCriteriaList);
        } catch (error: any) {
            showError('Não foi possível carregar os tipos de item e critérios de julgamento', error);
        }
    }

    // O endpoint não retorna total: página cheia indica que pode haver uma próxima
    async function loadItems() {
        if (isDraft) return;
        setLoading(true);
        try {
            const fetched = await IrpAppService.listIrpItems({ id: Number(irpId), page: page + 1, pageSize: ITEMS_PAGE_SIZE });
            setIrpItems(fetched);
            setHasNextPage(fetched.length === ITEMS_PAGE_SIZE);
        } catch (error: any) {
            showError('Não foi possível carregar os itens', error);
        } finally {
            setLoading(false);
        }
    }

    function handleOnChange(e: any) {
        const { name, value, checked } = e.target;
        const checkField = CHECK_BY_INPUT[name];
        if (checkField) {
            setItemIrp((previous) => ({ ...previous, [checkField]: checked }));
            return;
        }
        const field = FIELD_BY_INPUT[name];
        if (!field) return;
        setItemIrp((previous) => ({ ...previous, [field]: SANITIZED_INPUTS.has(name) ? sanitizeText(value) : value }));
    }

    function handleUnitValueChange(value: number) {
        setItemIrp((previous) => ({ ...previous, unitValueEstimated: value }));
    }

    function openItemDrawer(item: IIrpItemAddEditRequest) {
        setItemIrp(item);
        setShowValidationErrors(false);
        setOpenDrawer(true);
    }

    function addItem() {
        if (!modalityId) {
            showError('Antes de adicionar um item é necessário selecionar a modalidade');
            return;
        }
        openItemDrawer(EMPTY_ITEM);
    }

    function editItem(item: IIrpItemResponse) {
        openItemDrawer({ ...item, typeItem: item.typeItemId ?? item.typeItem });
    }

    function closeDrawer() {
        setOpenDrawer(false);
    }

    function resolveTypeItemDescription(typeItem?: number | null): string | undefined {
        if (typeItem === undefined || typeItem === null) return undefined;
        return materialService.find((element) => Number(element.id) === Number(typeItem))?.name;
    }

    function saveDraftItem(item: IIrpItemAddEditRequest) {
        const isNew = item.number === undefined;
        const number = isNew ? Math.max(0, ...irpItems.map((i) => i.number || 0)) + 1 : item.number;
        const draftItem = { ...toAddFormat(item), number, typeItemDescription: resolveTypeItemDescription(item.typeItem) } as IIrpItemResponse;
        setIrpItems(isNew ? [...irpItems, draftItem] : irpItems.map((i) => (i.number === number ? draftItem : i)));
        showSuccess(isNew ? 'Item adicionado com sucesso!' : 'Item alterado com sucesso!');
    }

    async function savePublishedItem(item: IIrpItemAddEditRequest) {
        if (item.number === undefined) {
            await IrpAppService.addItemsIrp(Number(irpId), [toAddFormat(item)]);
            showSuccess('Item adicionado com sucesso!');
        } else {
            await IrpAppService.updateItemsIrp(Number(irpId), [toRetifyFormat(item)], item.justification || '');
            showSuccess('Item retificado com sucesso!');
        }
        await loadItems();
    }

    async function saveItem() {
        if (isSaving) return;
        if (hasErrors(validateIrpItem(itemIrp, isRetification))) {
            setShowValidationErrors(true);
            return;
        }
        if (isDraft) {
            saveDraftItem(itemIrp);
            closeDrawer();
            return;
        }
        setIsSaving(true);
        try {
            await withLoading(() => savePublishedItem(itemIrp));
            closeDrawer();
        } catch (error: any) {
            showError(isRetification ? 'Não foi possível retificar o item' : 'Não foi possível adicionar o item', error);
        } finally {
            setIsSaving(false);
        }
    }

    function deleteItem(item: IIrpItemResponse) {
        if (item.number === undefined) return;
        const message = `Tem certeza que deseja excluir o item nº ${item.number}?`;
        if (isDraft) {
            confirmDelete(message, () => setIrpItems((items) => items.filter((i) => i.number !== item.number)));
            return;
        }
        confirmDeleteWithJustification(message, 'Informe a justificativa para excluir o item', async (justification) => {
            try {
                await withLoading(() => IrpAppService.deleteItemsIrp(Number(irpId), [item.number!], justification));
                showSuccess('Item excluído com sucesso!');
                await loadItems();
            } catch (error: any) {
                showError('Não foi possível excluir o item', error);
            }
        });
    }

    return {
        itemIrp,
        openDrawer,
        isSaving,
        validationErrors: showValidationErrors ? validateIrpItem(itemIrp, isRetification) : {},
        materialService,
        categoryItemPca,
        judgementCriteria,
        irpItems,
        isDraft,
        loading,
        isRetification,
        page,
        hasNextPage,
        itemsPageSize: ITEMS_PAGE_SIZE,
        handleOnChange,
        handleUnitValueChange,
        handlePageChange: setPage,
        addItem,
        editItem,
        closeDrawer,
        saveItem,
        deleteItem,
    };
};
